import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'
import * as domtoimage from 'dom-to-image';

import { Container, Content, BottomBar, IconBar } from '../../modules';
import { IAuthProps, Query } from '../../services';
import { Parameter, ParameterFactory, Station, StationFactory } from '../../resource';
import axios from 'axios';
import { App } from '../../App';
import { CartesianGrid, Legend, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import { XAxis } from 'recharts';
import { YAxis } from 'recharts';
import { Line } from 'recharts';
import { format, parse } from 'date-fns';
import { CustomTooltip } from './CustomTooltip';
import { Panel } from '@independent-software/typeui/controls/Panel';
import { Form } from '@independent-software/typeui/controls/Form';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Message } from '@independent-software/typeui/controls/Message';
import { Colors } from './ChartColors';
import { Button } from '@independent-software/typeui/controls/Button';
import { Export, TExportFormats } from './Export';

interface IState {
  series: Serie[];
  parameter: Parameter;
  parametersList: Parameter[];
  stations: Station[];
  stationsList: Station[];
}

interface Serie {
  name: string;
  color: string;
  data: any[];
}

class Analysis extends React.Component<IAuthProps, IState> {
  private wrapperRef: HTMLElement;

  constructor(props: IAuthProps) {
    super(props);
    this.state = {
      series: [],
      parameter: null,
      parametersList: [],
      stations: [],
      stationsList: []
    }
  }

  componentDidMount = () => {
    this.handleParameterSearch();
    this.handleStationSearch();
  }

  private handleExport = (format: TExportFormats) => {
    // Use export format to select matching function from domtoimage:
    let f = null;    
    switch(format) {
      case "png": f = (domtoimage as any).toPng; break; 
      case "jpg": f = (domtoimage as any).toJpeg; break;
      case "svg": f = (domtoimage as any).toSvg; break;
      default: return; /* unsupported format */
    }
    // Call export function, then use FileSaver to save to file.
    f(this.wrapperRef, { bgcolor: '#ffffff', cacheBust: true })
    .then(function (dataUrl:any) {
        window.saveAs(dataUrl, `chart.${format}`);
    });
  }  

  private handleParameterSearch = (q?: string) => {
    // Retrieve a list of parameters:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ParameterFactory.getSome(this.props.auth, 0, 999, query)
      .then((res) => this.setState({ parametersList: res.items }));
  }

  private handleChangeParameter = (parameter: Parameter) => {
    this.setState({
      parameter: parameter
    }, this.loadResults);
  }

  private handleStationSearch = (q?: string) => {
    // Retrieve a list of stations:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    StationFactory.getSome(this.props.auth, 0, 30, query)
      .then((res) => this.setState({ stationsList: res.items }));
  }

  private handleChangeStation = (stations: Station[]) => {
    this.setState({
      stations: stations
    }, this.loadResults);
  }

  private loadResults = () => {
    if(this.state.parameter == null || this.state.stations.length == 0) {
      this.setState({ series: [] });
      return;
    }

    const promises = this.state.stations.map((station, idx) => {
      return new Promise((resolve, reject) => {
        const query = new Query("value", "asc");
        query.setFilter("parameter", "eq", this.state.parameter.id);
        query.setFilter("station", "eq", station.id);
        let queryUrl = query.toUrl();
        if(queryUrl) queryUrl = '&' + queryUrl;    
        axios.get(`${App.apiURL}results/chart?api_token=${this.props.auth.token}${queryUrl}`)
        .then(res => {
          resolve({ name: station.name, color: Colors[idx % Colors.length], data: res.data.data });
        });
      });
    });

    Promise.all(promises)
    .then((series: Serie[]) => {
      this.setState({
        series: series
      });
    });
  }

  private getTicks = (data: Serie[], numTicks: number): number[] => {
    let minStamp: number = null;
    let maxStamp: number = null;
    data.forEach(s => {
      if(s.data.length < 2) return;
      minStamp = minStamp ? Math.min(minStamp, s.data[0].date) : s.data[0].date;
      maxStamp = maxStamp ? Math.max(maxStamp, s.data[s.data.length-1].date) : s.data[s.data.length-1].date;
    });

    if(minStamp == null || maxStamp == 0) return [];

    const ticks: number[] = [];
    for(let i = 0; i < numTicks; i++) {
      let date = Math.floor((maxStamp - minStamp)/(numTicks-1) * i + minStamp);
      ticks.push(date);
    }
    return ticks;
  }

  private chartReady = (): boolean => 
    this.state.parameter != null && this.state.stations.length > 0;

  render() {
    const filter = 
      <>
        <Panel.Content>
          <Form.Uncontrolled label="Parameter" hint="Select parameter">
            <Dropdown onSearch={this.handleParameterSearch} name="parameter" fluid value={this.state.parameter} data={this.state.parametersList} placeholder="Parameter" label={(item:Parameter) => item.name} onChange={this.handleChangeParameter}>
              <Dropdown.Column weight={1}>{(item:Parameter) => item.name}</Dropdown.Column>
            </Dropdown>
          </Form.Uncontrolled>
          <Form.Uncontrolled label="Station" hint="Select station">
            <Dropdown multiple onSearch={this.handleStationSearch} name="station" fluid value={this.state.stations} data={this.state.stationsList} placeholder="Station" label={(item:Station) => item.name} onChange={this.handleChangeStation}>
              <Dropdown.Column weight={1}>{(item:Station) => item.name}</Dropdown.Column>
            </Dropdown>
          </Form.Uncontrolled>
        </Panel.Content>
        <Panel.Footer>
          <Export disabled={!this.chartReady()} onExport={this.handleExport}/>
        </Panel.Footer>
      </>

    return (
      <Container>
        <>
          <Content>
            
            <IconBar>
              <Panel.Icon icon="tools" width={300}>
                {filter}
              </Panel.Icon>
            </IconBar>

            {!this.chartReady() &&
              <>
                <Underlay/>
                <Overlay>
                  <Message type="warning">
                    Please select a parameter and a station.
                  </Message>
                </Overlay>
              </>}

            {this.chartReady() &&
            <ChartHolder ref={(el:any) => this.wrapperRef = el}>
              <ResponsiveContainer width="100%" height="95%">
                <LineChart 
                  width={500} height={500} margin={{ top: 30, right: 40, left: 20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <Legend/>
                  <XAxis
                    dataKey="date" type="number" scale="time" allowDuplicatedCategory={false} domain={['dataMin', 'dataMax']}
                    ticks={this.getTicks(this.state.series, 8)}
                    tickFormatter={(stamp) => format(parse(stamp, "t", new Date()), "d MMM yy")}
                    interval={0}
                    padding={{ left: 50, right: 50 }}
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    unit={this.state.parameter.unit}
                  />
                  {this.state.series.map((s, index) =>
                    <Line key={index} type="linear" name={s.name} data={s.data} dataKey="value" strokeWidth={2} stroke={s.color} fill={"white"} activeDot={{ r: 8 }}/>
                  )}
                  <Tooltip isAnimationActive={false} content={<CustomTooltip parameter={this.state.parameter}/>} />
                </LineChart>
              </ResponsiveContainer>
            </ChartHolder>}
            
          </Content>
          <BottomBar>
          </BottomBar>
        </>
      </Container>
    );
  }
}

// Needed to get a reference for domtoimage.
const ChartHolder = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
`

const Underlay = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #333;
  z-index: 1;
  opacity: 0.5;
`

const Overlay = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  left: 10%;
  top: 0;
  right: 10%;
  bottom: 0;
  background: transparent;
  z-index: 1;
`

export { Analysis };
